/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Breadcrumb } from '@fluentui/react/lib/Breadcrumb';
import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { Check, ICheckStyleProps, ICheckStyles } from '@fluentui/react/lib/Check';
import { Checkbox, ICheckboxStyleProps, ICheckboxStyles } from '@fluentui/react/lib/Checkbox';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { ColumnActionsMode, DetailsHeader, IColumn, IDetailsHeaderProps } from '@fluentui/react/lib/DetailsList';
import { Icon } from '@fluentui/react/lib/Icon';
import { IScrollablePaneProps, IScrollablePaneStyles, ScrollablePane } from '@fluentui/react/lib/ScrollablePane';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { Sticky } from '@fluentui/react/lib/Sticky';
import { DefaultPalette, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction, IRenderFunction, SelectionMode } from '@fluentui/react/lib/Utilities';
import orderBy from 'lodash.orderby';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { logDTButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { getScopedTheme, getStylistV2, important } from 'src/core/themes/luisStylist';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { getReadableSize } from 'src/core/utils/getReadableSize';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import {
    getCurrentFolders,
    getSplittedPath,
    getUniqueBlobFiles,
    hasSamePath,
    isFolderRow
} from 'src/translation/utils/translationBlobStoreTableUtils';

const { getClassName } = getStylistV2('TranslationStorageFileTable');
const themes = getScopedTheme('TranslationStorageFileTable');

const itemClassName = getClassName('item', important({ fontSize: '14px', fontWeight: 'normal' }));
const linkItemClassName = getClassName('linkItem', theme =>
    important({ color: theme.linkColor, fontSize: '14px', display: 'inline-block', height: '100%' })
);

const breadcrumbStyles = {
    item: { fontSize: '14px', fontWeight: 'normal' },
    chevron: { fontSize: '10px' },
    root: { flex: 1 },
    itemLink: `${itemClassName} ${linkItemClassName}`
};

export type TranslationBlobStoreTableProps = {
    files: AzureBlobStore[];
    isFilesLoading: boolean;
    selectedBlobFiles: AzureBlobStore[];
    isAllFilesSelected: boolean;
    disableSearch?: boolean;
    hiddenColumnKeys?: string[];
    selectedContainer: string;
    downloadFile?: (fileName: string) => void;
    onChangeSelectedBlobFiles: (selectedBlobFiles: AzureBlobStore[]) => void;
    onChangeIsAllFilesSelected: (isAllFilesSelected: boolean) => void;
};

const messages = defineMessages({
    fileNameHeader: { id: 'translation.translationBlobStoreTable.fileNameHeader', defaultMessage: 'Name' },
    emptyFileName: { id: 'translation.translationBlobStoreTable.emptyFileName', defaultMessage: '<no name>' },
    fileSizeHeader: { id: 'translation.translationBlobStoreTable.fileSizeHeader', defaultMessage: 'File size (KB)' },
    downloadFileBtn: { id: 'translation.translationBlobStoreTable.downloadFileBtn', defaultMessage: 'Download file' },
    lastModifiedHeader: { id: 'translation.translationBlobStoreTable.lastModifiedHeader', defaultMessage: 'Last modified' },
    toggleSelectFile: { id: 'translation.translationBlobStoreTable.toggleSelectFile', defaultMessage: 'Toggle Select file' },
    searchForFileFieldPlaceholder: {
        id: 'translation.translationBlobStoreTable.searchForFileFieldPlaceholder',
        defaultMessage: 'Search for a file...'
    },
    searchFieldPlaceholder: { id: 'translation.translationBlobStoreTable.searchFieldPlaceholder', defaultMessage: 'Search...' },
    blobStorageTablePlaceholder: {
        id: 'translation.translationBlobStoreTable.blobStorageTablePlaceholder',
        defaultMessage: 'Select a container to add files'
    },
    toggleSelectAllFiles: {
        id: 'translation.translationBlobStoreTable.toggleSelectAllFiles',
        defaultMessage: 'Toggle select all files in container'
    }
});

const paginationStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({ root: { margin: '10px 0 !important' } });
const checkboxStyles = classNamesFunction<ICheckboxStyleProps, ICheckboxStyles>()({
    checkbox: { height: '18px', width: '18px', borderRadius: '100%' }
});
const checkedMarkStyles = (checked: boolean) =>
    mergeStyleSets(
        checkboxStyles,
        classNamesFunction<ICheckStyleProps, ICheckStyles>()({
            root: { marginTop: '11px' },
            check: checked && { fontSize: '28px', fontWeight: '400' },
            circle: checked && {
                color: DefaultPalette.themePrimary,
                background: DefaultPalette.themePrimary,
                borderRadius: '50%'
            }
        })
    );

const scrollablePaneStyles = classNamesFunction<IScrollablePaneProps, IScrollablePaneStyles>()({
    root: { height: '100%', position: 'relative' }
});
const tableTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }
});

export const TranslationBlobStoreTable = (props: TranslationBlobStoreTableProps) => {
    const {
        files,
        isFilesLoading,
        selectedBlobFiles,
        selectedContainer,
        isAllFilesSelected,
        disableSearch = false,
        hiddenColumnKeys = [],
        downloadFile,
        onChangeIsAllFilesSelected,
        onChangeSelectedBlobFiles
    } = props;

    const { localize, locale } = useLocalization();
    const [prevFolderNames, setPrevFolderNames] = React.useState<string[]>([]);

    const [sortKey, setSortKey] = React.useState<SortKeyType<AzureBlobStore>>({ key: 'name', order: 'asc' });
    const [query, setQuery] = React.useState('');

    const breadCrumbItems = React.useMemo(
        () => [
            { text: selectedContainer, key: selectedContainer, onClick: () => setPrevFolderNames([]) },
            ...prevFolderNames.map((prev, index) => ({
                text: prev,
                key: prev,
                onClick: () => setPrevFolderNames(prevFolderNames.slice(0, index + 1))
            }))
        ],
        [prevFolderNames]
    );

    const currentFolders = React.useMemo(() => getCurrentFolders(files, prevFolderNames), [prevFolderNames]);

    const filteredFiles = substringSearchStrategy(FeatureToggles.DtFileLocationBreadCrumb ? currentFolders : files, query, 'name');

    const sortedFiles = orderBy(filteredFiles, [createKeyedStringNormalizer(sortKey.key)], [sortKey.order]);

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(sortedFiles, 7);

    React.useEffect(() => setPrevFolderNames([]), [files]);

    React.useEffect(() => setQuery(''), [prevFolderNames]);

    React.useEffect(() => {
        const isChecked = currentFolders.length > 0 && currentFolders.every(item => isItemChecked(item));
        onChangeIsAllFilesSelected(isChecked);
    }, [currentFolders, selectedBlobFiles]);

    const onSortColumn = (c: IColumn) => {
        setSortKey({ key: c.key as keyof AzureBlobStore, order: c.isSortedDescending ? 'asc' : 'desc' });
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };
    const isItemChecked = (item: AzureBlobStore): boolean => {
        const itemPath = getSplittedPath(item.id);

        const occurrenceInFiles = files.filter(file => hasSamePath(itemPath, getSplittedPath(file.name))).length;

        const occurrenceInSelections = selectedBlobFiles.filter(file => hasSamePath(itemPath, getSplittedPath(file.id))).length;

        return occurrenceInFiles === occurrenceInSelections;
    };

    const onChangeBlobSelection = (item: AzureBlobStore[], isChecked: boolean) => {
        if (isChecked) {
            onChangeSelectedBlobFiles(getUniqueBlobFiles(selectedBlobFiles, item));
        } else {
            onChangeSelectedBlobFiles(selectedBlobFiles.filter(f => !item.some(it => it.id === f.id)));
        }
    };

    const onChangeAllBlobSelection = (isChecked: boolean) => {
        onChangeIsAllFilesSelected(isChecked);
        if (isChecked) {
            const currentFiles = files.filter(file => hasSamePath(prevFolderNames, getSplittedPath(file.name)));
            const selectedFiles = getUniqueBlobFiles(selectedBlobFiles, currentFiles);
            onChangeSelectedBlobFiles(selectedFiles);
        } else {
            const currentFiles = selectedBlobFiles.filter(blob => !hasSamePath(prevFolderNames, getSplittedPath(blob.id)));
            onChangeSelectedBlobFiles(currentFiles);
        }
    };

    const handleOnchangeSelection = (item: AzureBlobStore, checked: boolean) => {
        const currentPath = [...prevFolderNames, item.name];

        const currentFiles = files.filter(folder => hasSamePath(currentPath, getSplittedPath(folder.name)));
        onChangeBlobSelection(currentFiles, checked);
    };

    const filesTableColumn: IColumn[] = [
        {
            key: 'selection',
            name: 'selection',
            minWidth: 18,
            maxWidth: 18,
            isResizable: false,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item: AzureBlobStore) => (
                <Checkbox
                    styles={checkboxStyles}
                    ariaLabel={localize(messages.toggleSelectFile)}
                    checked={
                        FeatureToggles.DtFileLocationBreadCrumb ? isItemChecked(item) : selectedBlobFiles.some(f => f.name === item.name)
                    }
                    onChange={(_, checked) =>
                        FeatureToggles.DtFileLocationBreadCrumb
                            ? handleOnchangeSelection(item, checked)
                            : onChangeBlobSelection([item], checked)
                    }
                />
            ),
            onRenderHeader: () => (
                <div role="table">
                    <div role="row">
                        <div
                            role="columnheader"
                            aria-label={localize(messages.toggleSelectAllFiles)}
                            onClick={e => {
                                e.stopPropagation();
                                onChangeAllBlobSelection(!isAllFilesSelected);
                            }}>
                            <Check checked={isAllFilesSelected} styles={checkedMarkStyles(isAllFilesSelected)} />
                        </div>
                    </div>
                </div>
            )
        },
        {
            minWidth: 200,
            key: 'name',
            fieldName: 'name',
            isResizable: true,
            isSorted: sortKey?.key === 'name',
            name: localize(messages.fileNameHeader),
            isSortedDescending: sortKey?.key === 'name' && sortKey?.order === 'desc',
            onRender: (item: AzureBlobStore) => (
                <Stack horizontal verticalAlign="center" title={item.name} tokens={{ childrenGap: 20, maxWidth: '100%' }}>
                    <Icon
                        iconName={isFolderRow(item.blobType) ? 'FabricFolderFill' : 'TextDocument'}
                        styles={{
                            root: { fontSize: '14px', color: isFolderRow(item.blobType) ? themes.folderIconColor : themes.fileIconColor }
                        }}
                    />
                    {isFolderRow(item.blobType) ? (
                        <ActionButton
                            styles={{ root: { overflow: 'hidden' } }}
                            onClick={() => setPrevFolderNames([...prevFolderNames, item.name])}>
                            {item.name.length > 0 ? item.name : localize(messages.emptyFileName)}
                        </ActionButton>
                    ) : (
                        <LuisText styles={{ root: { overflow: 'hidden' } }}>{item.name}</LuisText>
                    )}
                </Stack>
            )
        },
        {
            minWidth: 120,
            isResizable: true,
            key: 'modificationDate',
            fieldName: 'modificationDate',
            name: localize(messages.lastModifiedHeader),
            isSorted: sortKey?.key === 'modificationDate',
            isSortedDescending: sortKey?.key === 'modificationDate' && sortKey?.order === 'desc',
            onRender: (item: AzureBlobStore) => (
                <LuisText styles={{ root: { overflow: 'hidden' } }}>
                    {!isFolderRow(item.blobType) && (item.modificationDate?.toLocaleDateString(locale) ?? '-')}
                </LuisText>
            )
        },
        {
            minWidth: 130,
            key: 'size',
            isResizable: true,
            fieldName: 'size',
            name: localize(messages.fileSizeHeader),
            isSorted: sortKey?.key === 'size',
            isSortedDescending: sortKey?.key === 'size' && sortKey?.order === 'desc',
            onRender: (item: AzureBlobStore) => (
                <LuisText title={getReadableSize(item.size)} styles={tableTextStyles}>
                    {!isFolderRow(item.blobType) && getReadableSize(item.size)}
                </LuisText>
            )
        },
        {
            key: 'downloadButton',
            name: 'downloadButton',
            minWidth: 18,
            maxWidth: 18,
            isResizable: false,
            isIconOnly: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item: AzureBlobStore) =>
                !isFolderRow(item.blobType) && (
                    <IconButton
                        id={'downloadButton'}
                        iconProps={{ iconName: 'Download' }}
                        title={localize(messages.downloadFileBtn)}
                        ariaLabel={localize(messages.downloadFileBtn)}
                        data-automation-id="downloadButton"
                        onClick={() => {
                            downloadFile(item.id);
                            logDTButtonClick('document translation', 'jobCreation', 'blobStorage', 'Button:download', 'filesSelection');
                        }}
                    />
                )
        }
    ];

    const commandBarFarItems: ICommandBarItemProps[] = [
        {
            key: 'search',
            onRender: () => (
                <LuisDebouncedCollapsableSearchBox
                    size={18}
                    value={query}
                    alwaysExpanded
                    role="menuitem"
                    disableAnimation
                    disabled={!files.length}
                    ariaLabel={localize(
                        FeatureToggles.DtFileLocationBreadCrumb ? messages.searchFieldPlaceholder : messages.searchForFileFieldPlaceholder
                    )}
                    placeholder={localize(
                        FeatureToggles.DtFileLocationBreadCrumb ? messages.searchFieldPlaceholder : messages.searchForFileFieldPlaceholder
                    )}
                    onChange={(_, newValue?: string) => setQuery(newValue)}
                />
            ),
            ariaLabel: localize(
                FeatureToggles.DtFileLocationBreadCrumb ? messages.searchFieldPlaceholder : messages.searchForFileFieldPlaceholder
            )
        }
    ];

    const onRenderHeader: IRenderFunction<IDetailsHeaderProps> = headerProps => (
        <Sticky>
            <DetailsHeader {...headerProps} />
        </Sticky>
    );

    return (
        <Stack grow>
            {FeatureToggles.DtFileLocationBreadCrumb ? (
                <Stack styles={{ root: { borderBottom: `1px solid ${themes.borderColor}` } }} horizontal grow>
                    <Stack styles={{ root: { flex: 1 } }}>
                        <Breadcrumb items={breadCrumbItems} styles={breadcrumbStyles} />
                    </Stack>
                    {!disableSearch && <LuisCommandBar showTopBorder={false} items={[]} farItems={commandBarFarItems} />}
                </Stack>
            ) : (
                <LuisCommandBar items={commandBarFarItems} />
            )}

            <ScrollablePane styles={mergeStyleSets(scrollablePaneStyles, { root: { minHeight: '250px' } })}>
                <Table<AzureBlobStore>
                    selectionMode={SelectionMode.none}
                    items={pageItems}
                    columns={filesTableColumn.filter(c => !hiddenColumnKeys.includes(c.key))}
                    onSortColumn={onSortColumn}
                    isLoading={isFilesLoading}
                    onRenderDetailsHeader={onRenderHeader}
                    emptyPlaceholder={localize(messages.blobStorageTablePlaceholder)}
                />
            </ScrollablePane>
            <TablePagination
                pageCount={pageCount}
                currentPage={currentPage}
                containerStyles={paginationStyles}
                onPageChange={p => handlePageChange(p.selected)}
            />
        </Stack>
    );
};
